import axios from 'utils/bsac-api.js';
import conversions from 'utils/conversions.js';
import defaultValues from 'constant/partner/default-values';
import environments from 'constant/environments.js';

const state = () => Object.assign({}, defaultValues);

const getters = {
  /**
   * 認証情報を取得する
   * @param {*} state
   * @returns
   */
  auth: (state) => state.auth,
  /**
   * 認証用ヘッダーを取得する
   * @param {*} getters
   * @returns
   */
  authHeaders: (getters) => {
    if (!getters.auth) return null;
    return {
      'access-token': getters.auth['access-token'],
      client: getters.auth['client'],
      uid: getters.auth['uid'],
    };
  },
  /**
   * パートナーIDを取得する
   * @param {*} state
   * @returns
   */
  id: (state) => state.id,
  /**
   *　認証状態を取得する
   * @param {*} state
   * @returns
   */
  isAuthenticated: (state) => state.auth !== null,
  /**
   * パートナー情報を取得する
   * @param {*} state
   * @returns
   */
  partner: (state) => state.partner,
  /**
   * パートナー名を取得する
   * @param {*} state
   * @returns
   */
  partnerName: (state) => {
    if (!state.partner) return 'ゲスト';
    if (!state.partner.nameJa) return 'ゲスト';
    return state.partner.nameJa;
  },
  /**
   * パートナー種別を取得する
   * @param {*} state
   * @returns
   */
  partnerType: (state) => state.partnerType,
};

const mutations = {
  /**
   * IDをセットする
   * @param {*} state
   * @param {*} id
   */
  setId(state, id) {
    state.id = parseInt(id);
  },
  /**
   * ログイン情報をセットする
   * @param {*} state
   * @param {*} headers
   */
  setAuth(state, headers) {
    state.auth = conversions.toCamelCaseForObject(headers);
  },
  /**
   * ログイン情報をセットする
   * @param {*} state
   * @param {*} data
   */
  setParter(state, data) {
    let partner = Object.assign({}, defaultValues.partner);

    if (!data) return;
    partner.id = state.id ? state.id : null;
    partner.nameJa = data.name_ja ? data.name_ja : null;
    partner.nameKana = data.name_kana ? data.name_kana : null;
    partner.postCode = data.postcode_without_hyphen ? data.postcode_without_hyphen : null;
    partner.prefecture = data.prefecture ? data.prefecture : null;
    partner.address1st = data.address_1st ? data.address_1st : null;
    partner.address2nd = data.address_2nd ? data.address_2nd : null;
    partner.phoneNo = data.phone_no ? data.phone_no : null;
    if (!partner.phoneNo && data.mobile_no) partner.phoneNo = data.mobile_no;
    partner.faxNo = data.fax_no ? data.fax_no : null;
    if (!process.env.SHOPPING_ORDER_MAIL_PARTNER_TO) {
      partner.email = data.email ? data.email : null;
    } else {
      partner.email = process.env.SHOPPING_ORDER_MAIL_PARTNER_TO;
    }

    state.partner = partner;
  },
  /**
   * パートナー種別をセットする
   * @param {*} state
   * @param {*} id
   */
  setPartnerType(state, type) {
    state.partnerType = type;
  },
  /**
   * ログイン情報をクリアする
   * @param {*} state
   */
  clearData(state) {
    state.id = null;
    state.partner = null;
    state.auth = null;
    state.partnerType = null;
  },
};

const actions = {
  /**
   * APIを呼び出し商品一覧を取得する
   * @param {*} param0
   * @param {*} state
   * @returns
   */
  getPartner({ commit, getters }) {
    const headers = getters['authHeaders'] ? { headers: getters['authHeaders'] } : null;
    // console.log(`header: ${JSON.stringify(headers)}`);
    return axios
      .get(`${environments.shoppingApiUrl}/partners/${getters['id']}`, headers)
      .then((response) => {
        // console.debug(`response: ${JSON.stringify(response)}`);
        commit('setParter', response.data.data);
        return {
          success: true,
          message: null,
        };
      })
      .catch((error) => {
        // console.debug(`error: ${JSON.stringify(error)}`);
        return {
          success: false,
          message: 'シングルサインオンに失敗しました。ログインした状態で閲覧したい場合は、登録更新手続きをお願い致します。[X4863PG9]',
        };
      });
  },
  /**
   * ログインする
   * @param {*} param0
   * @param {*} payload
   * @returns
   */
  signin({ commit, dispatch }, payload) {
    // console.log(JSON.stringify(payload));
    return axios
      .post(`${environments.partnerAuthUrl}/sign_in`, {
        login_id: payload.loginId,
        password: payload.password,
      })
      .then((response) => {
        // console.debug(`response: ${JSON.stringify(response)}`);
        commit('setId', response.data.data.id);
        commit('setAuth', response.headers);
        commit('setPartnerType', response.data.data.partner_type);
        dispatch('getPartner');
        return {
          success: true,
          message: null,
        };
      })
      .catch((error) => {
        // console.debug(`error: ${JSON.stringify(error)}`);
        return {
          success: false,
          message: 'ログインID、またはパスワードが異なります。[T9R2B3ZE]',
        };
      });
  },
  /**
   * ログアウトする
   * @param {*} param0
   * @returns
   */
  signout({ commit, getters }) {
    const headers = getters['authHeaders'] ? { headers: getters['authHeaders'] } : null;
    // console.log(JSON.stringify(headers));
    if (!headers) return;
    return axios
      .delete(`${environments.partnerAuthUrl}/sign_out`, headers)
      .then((response) => {
        // console.debug(`response: ${JSON.stringify(response)}`);
        commit('clearData');
        return {
          success: true,
          message: null,
        };
      })
      .catch((error) => {
        // console.debug(`error: ${JSON.stringify(error)}`);
        return {
          success: false,
          message: 'ログアウトに失敗しました。[D49AVMQK]',
        };
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
